import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ENVIRONMENT } from '@joyinvoice/web/shared/util';
import { PermissionsService } from '@joyinvoice/web/shared/data-access';
import type { User, Tenant } from '@joyinvoice/web/shared/util';

export interface LoginDto {
  email: string;
  password: string;
}

export interface RegisterDto {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  companyName: string;
  subdomain: string;
  phone?: string;
  timezone?: string;
  currency?: string;
}

interface AuthResponse {
  user: User & { permissions?: string[], roleEntity?: { permissions?: string[] } };
  tenant: Tenant;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly permissionsService = inject(PermissionsService);
  private readonly baseUrl = `${inject(ENVIRONMENT).apiUrl}/auth`;

  // State
  private readonly _user = signal<User | null>(null);
  private readonly _tenant = signal<Tenant | null>(null);
  private readonly _isLoading = signal(true); // default true for initial load

  // Public signals
  readonly currentUser = this._user.asReadonly();
  readonly currentTenant = this._tenant.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly isAuthenticated = computed(() => !!this._user());
  readonly userFullName = computed(() => {
    const user = this._user();
    return user ? `${user.firstName} ${user.lastName}` : '';
  });
  readonly userInitials = computed(() => {
    const user = this._user();
    return user ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase() : '';
  });

  constructor() {
  }

  async initializeAuth(): Promise<void> {
    this._isLoading.set(true);
    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: User & { permissions?: string[], roleEntity?: { permissions?: string[] }, tenant?: Tenant } }>(`${this.baseUrl}/me`)
      );
      const user = response.data;
      this._user.set(user);
      if (user.tenant) {
        this._tenant.set(user.tenant as Tenant);
      }
      
      const perms = user.permissions || user.roleEntity?.permissions || [];
      this.permissionsService.setPermissions(user.role, perms);
    } catch {
      this.logoutLocal();
    } finally {
      this._isLoading.set(false);
    }
  }

  async login(dto: LoginDto): Promise<void> {
    this._isLoading.set(true);
    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; data: AuthResponse }>(`${this.baseUrl}/login`, dto)
      );
      this.handleAuthResponse(response.data);
    } finally {
      this._isLoading.set(false);
    }
  }

  async register(dto: RegisterDto): Promise<void> {
    this._isLoading.set(true);
    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; data: AuthResponse }>(`${this.baseUrl}/register`, dto)
      );
      this.handleAuthResponse(response.data);
    } finally {
      this._isLoading.set(false);
    }
  }

  async refreshTokens(): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.post<{ success: boolean }>(`${this.baseUrl}/refresh`, {})
      );
      await this.initializeAuth();
      return true;
    } catch {
      this.logoutLocal();
      return false;
    }
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(this.http.post(`${this.baseUrl}/logout`, {}));
    } catch { /* ignore */ }
    this.logoutLocal();
    this.router.navigate(['/auth/login']);
  }

  private logoutLocal(): void {
    this._user.set(null);
    this._tenant.set(null);
    this.permissionsService.clear();
  }

  private handleAuthResponse(data: AuthResponse): void {
    this._user.set(data.user);
    this._tenant.set(data.tenant as Tenant);

    // Initialise permissions from the login payload
    const perms = data.user.permissions || data.user.roleEntity?.permissions || [];
    this.permissionsService.setPermissions(data.user.role, perms);
  }
}
